import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getSupportedLanguages } from '../lib/i18n';
import { generateStructuredData } from '../lib/seoStructuredData';

const SEO = ({
  // Centralized page key: when provided, default values are derived per page
  page,
  title = 'Kreatives - Nordic Creative Talent Platform',
  description = 'Connect with talented Nordic creatives. Find freelancers, designers, developers, and creative professionals across Sweden, Norway, Denmark, Finland, and Iceland.',
  keywords = 'nordic creatives, freelancers, designers, developers, creative services, sweden, norway, denmark, finland, iceland',
  image = '/logo-kreativ-light.png',
  url,
  type = 'website',
  author = 'Kreatives',
  publishedTime,
  modifiedTime,
  section,
  tags = [],
  locale = 'en_US',
  siteName = 'Kreatives',
  twitterHandle = '@kreatives_nordic',
  structuredData,
  canonical,
  noindex = false,
  nofollow = false,
  alternateLanguages = []
}) => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const currentUrl = url || `${window.location.origin}${location.pathname}`;
  const canonicalUrl = canonical || currentUrl;

  // Derive defaults when `page` is provided; explicit props still override
  const derivePageDefaults = (p) => {
    const defaults = { title: undefined, description: undefined, keywords: undefined, type: 'website', image: '/logo-kreativ-light.png', structuredData: undefined };
    try {
      switch (p) {
        case 'home':
          defaults.title = `${t('nav.home')} – Kreatives`;
          defaults.description = t('services.ourServicesDescription') || 'Nordic Creative Talent Platform';
          defaults.structuredData = generateStructuredData.homepage();
          break;
        case 'contact':
          defaults.title = `${t('footer.contact')} – Kreatives`;
          defaults.description = 'Get in touch with the Kreatives team';
          defaults.structuredData = generateStructuredData.contact();
          break;
        case 'login':
          defaults.title = `${t('auth.login.title')} – Kreatives`;
          defaults.description = 'Sign in to your Kreatives account';
          defaults.type = 'website';
          defaults.structuredData = {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": t('auth.login.title'),
            "description": defaults.description
          };
          break;
        case 'register':
          defaults.title = 'Create account – Kreatives';
          defaults.description = 'Join Kreatives and connect with Nordic creative opportunities';
          defaults.type = 'website';
          break;
        default:
          break;
      }
    } catch { /* noop */ }
    return defaults;
  };

  const pageDefaults = page ? derivePageDefaults(page) : {};
  title = title ?? pageDefaults.title ?? title;
  description = description ?? pageDefaults.description ?? description;
  keywords = keywords ?? pageDefaults.keywords ?? keywords;
  type = type ?? pageDefaults.type ?? type;
  image = image ?? pageDefaults.image ?? image;
  structuredData = structuredData ?? pageDefaults.structuredData ?? structuredData;

  // Auto-build alternate language links if none provided
  if (!alternateLanguages || alternateLanguages.length === 0) {
    try {
      const langs = getSupportedLanguages();
      const urlObj = new URL(currentUrl);
      // Provide x-default without lng param
      const baseHref = `${urlObj.origin}${urlObj.pathname}`;
      alternateLanguages = [{ lang: 'x-default', href: baseHref }].concat(
        langs.map((code) => {
          const u = new URL(baseHref);
          u.searchParams.set('lng', code);
          return { lang: code, href: u.toString() };
        })
      );
    } catch { /* noop */ }
  }

  useEffect(() => {
    // Update document title
    document.title = title;

    // Clear existing meta tags that we manage
    const managedTags = [
      'description', 'keywords', 'author', 'robots',
      'og:title', 'og:description', 'og:image', 'og:url', 'og:type', 'og:site_name', 'og:locale',
      'og:article:author', 'og:article:published_time', 'og:article:modified_time', 'og:article:section', 'og:article:tag',
      'twitter:card', 'twitter:title', 'twitter:description', 'twitter:image', 'twitter:site', 'twitter:creator',
      'canonical'
    ];

    managedTags.forEach(name => {
      const existing = document.querySelector(`meta[name="${name}"], meta[property="${name}"], link[rel="${name}"]`);
      if (existing) existing.remove();
    });

    // Remove existing structured data
    const existingStructuredData = document.querySelectorAll('script[type="application/ld+json"]');
    existingStructuredData.forEach(script => script.remove());

    // Basic meta tags
    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'author', content: author },
      { name: 'robots', content: `${noindex ? 'noindex' : 'index'},${nofollow ? 'nofollow' : 'follow'}` }
    ];

    // Open Graph tags
    const ogTags = [
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: image.startsWith('http') ? image : `${window.location.origin}${image}` },
      { property: 'og:url', content: currentUrl },
      { property: 'og:type', content: type },
      { property: 'og:site_name', content: siteName },
      { property: 'og:locale', content: locale }
    ];

    // Article-specific Open Graph tags
    if (type === 'article') {
      if (author) ogTags.push({ property: 'og:article:author', content: author });
      if (publishedTime) ogTags.push({ property: 'og:article:published_time', content: publishedTime });
      if (modifiedTime) ogTags.push({ property: 'og:article:modified_time', content: modifiedTime });
      if (section) ogTags.push({ property: 'og:article:section', content: section });
      tags.forEach(tag => ogTags.push({ property: 'og:article:tag', content: tag }));
    }

    // Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: image.startsWith('http') ? image : `${window.location.origin}${image}` },
      { name: 'twitter:site', content: twitterHandle },
      { name: 'twitter:creator', content: twitterHandle }
    ];

    // Create and append meta tags
    [...metaTags, ...ogTags, ...twitterTags].forEach(({ name, property, content }) => {
      if (content) {
        const meta = document.createElement('meta');
        if (name) meta.setAttribute('name', name);
        if (property) meta.setAttribute('property', property);
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    });

    // Canonical link
    const canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', canonicalUrl);
    document.head.appendChild(canonicalLink);

    // Alternate language links
    alternateLanguages.forEach(({ lang, href }) => {
      const alternateLink = document.createElement('link');
      alternateLink.setAttribute('rel', 'alternate');
      alternateLink.setAttribute('hreflang', lang);
      alternateLink.setAttribute('href', href);
      document.head.appendChild(alternateLink);
    });

    // Structured data
    if (structuredData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Default structured data for the organization
    const defaultStructuredData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Kreatives",
      "description": "Nordic Creative Talent Platform connecting businesses with talented freelancers across Sweden, Norway, Denmark, Finland, and Iceland.",
      "url": window.location.origin,
      "logo": `${window.location.origin}/logo-kreativ-light.png`,
      "sameAs": [
        "https://twitter.com/kreatives_nordic",
        "https://linkedin.com/company/kreatives-nordic"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "url": `${window.location.origin}/contact`,
        "email": "info@kreatives.app",
        "availableLanguage": ["en", "se", "dk", "no", "fi", "is"]
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Hans Michelsensgatan 9",
        "addressLocality": "Malmö",
        "postalCode": "211 20",
        "addressCountry": "SE"
      },
      "areaServed": [
        {
          "@type": "Country",
          "name": "Sweden"
        },
        {
          "@type": "Country", 
          "name": "Norway"
        },
        {
          "@type": "Country",
          "name": "Denmark"
        },
        {
          "@type": "Country",
          "name": "Finland"
        },
        {
          "@type": "Country",
          "name": "Iceland"
        }
      ]
    };

    if (!structuredData) {
      const defaultScript = document.createElement('script');
      defaultScript.type = 'application/ld+json';
      defaultScript.textContent = JSON.stringify(defaultStructuredData);
      document.head.appendChild(defaultScript);
    }

  }, [title, description, keywords, image, currentUrl, canonicalUrl, type, author, publishedTime, modifiedTime, section, tags, locale, siteName, twitterHandle, structuredData, noindex, nofollow, alternateLanguages, i18n.language]);

  return null; // This component doesn't render anything
};

export default SEO;
